// Persistent registry of Smart Tagging assistant sessions (AI Tag Automation,
// initiatives/ai-tag-automation). Smart Tagging is a regular dock chat session
// with extra branding (header, wizard empty state, wider panel); the dock
// context rebuilds sessions from remote thread summaries, so the "this thread
// is a Smart Tagging session" fact must live OUTSIDE the session object —
// keyed by thread id here and persisted so it survives reloads and history
// re-opens.
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

const STORAGE_KEY = 'csDashboardSmartTaggingSessionIds';
const MAX_TRACKED_SESSIONS = 100;

// Keep in sync with DASHBOARD_ASSISTANT_ENABLED in
// DashboardAssistantProvider.jsx (not imported to keep this module .ts-clean —
// the provider is untyped .jsx).
const ASSISTANT_ENABLED =
  (import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_ENABLED ||
    import.meta.env.VITE_DASHBOARD_ASSISTANT_ENABLED) !== 'false';

const SMART_TAGGING_FLAG = 'smart_tagging';

// Single gate for every Smart Tagging entry point: the assistant must be
// enabled AND the PostHog `smart_tagging` flag on. Mirrors the flag-read
// pattern in useMarketingTierEnabled (isFeatureEnabled + onFeatureFlags
// subscription); 'undefined' (not yet resolved / blocked) → off.
export function useSmartTaggingEnabled(): boolean {
  const [flagEnabled, setFlagEnabled] = useState(() =>
    Boolean(posthog.isFeatureEnabled(SMART_TAGGING_FLAG)),
  );
  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setFlagEnabled(Boolean(posthog.isFeatureEnabled(SMART_TAGGING_FLAG)));
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);
  return ASSISTANT_ENABLED && flagEnabled;
}

function readIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeIds(ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-MAX_TRACKED_SESSIONS)));
  } catch {
    // Storage full/blocked — branding degrades gracefully to a normal chat.
  }
}

export function markSmartTaggingSession(sessionId: string) {
  if (!sessionId) return;
  const ids = readIds();
  if (!ids.includes(sessionId)) {
    writeIds([...ids, sessionId]);
  }
}

export function isSmartTaggingSession(sessionId: string | null | undefined): boolean {
  return Boolean(sessionId) && readIds().includes(sessionId as string);
}
